import type { UnknownAction } from 'redux'
import type { ThunkAction } from 'redux-thunk'
import type { Dispatch, ReduxState } from '@/utils/typeHelpers'
import { logHeardError, parseErrorCodeFromCatch } from '@/utils/errorHelpers'
import { apiAxios } from '@/api'
import { fetchWrapper } from '@/reducers/fetch'
import {
  setFirstConversationsPage,
  appendConversations,
  conversationViewPending,
  conversationViewFailed,
  conversationViewPageLoaded,
  conversationViewReloaded,
  removeConversation,
  setActiveConversationId,
  setActiveView,
  conversationResumed,
} from '@/features/adviser-chat/adviser-chat.slice'
import { updateYourTurnCount } from '@/components/conversations/conversation.slice'
import {
  ADVISER_API_PATH,
  CATCH_UP_MONTHS_PATH,
  CONVERSATION_VIEWS,
  type ConversationView,
} from './lib/constants'
import type {
  CatchUpWindow,
  ConversationDetailResponse,
  ConversationSummary,
  FeedbackPayload,
  MessageTab,
  MessageThreadCursor,
  MessageThreadDetail,
  MessageThreadsResponse,
} from './lib/types'

type AdviserThunk<R = void> = ThunkAction<
  Promise<R>,
  ReduxState,
  void,
  UnknownAction
>

export const fetchConversations =
  (offset = 0, options: { throwOnError?: boolean } = {}): AdviserThunk =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await apiAxios.get<{
        conversations: ConversationSummary[]
        hasMore: boolean
      }>(`${ADVISER_API_PATH}/conversations`, { params: { offset } })
      dispatch(
        (offset ? appendConversations : setFirstConversationsPage)({
          conversations: data.conversations,
          hasMore: data.hasMore,
        })
      )
    } catch (err) {
      logHeardError(err, { context: 'adviser: fetch conversations' })
      if (options.throwOnError) throw err
      // Mark loaded even on error so the UI can show empty state / mock data
      // instead of being stuck in a permanent loading state.
      if (!offset)
        dispatch(
          setFirstConversationsPage({ conversations: [], hasMore: false })
        )
    }
  }

interface ConversationViewResponse {
  conversations: ConversationSummary[]
  hasMore: boolean
  needsAttentionCount?: number
}

const getConversationView = async (
  view: ConversationView,
  offset: number
): Promise<ConversationViewResponse> => {
  const { data } = await apiAxios.get<ConversationViewResponse>(
    `${ADVISER_API_PATH}/conversations`,
    { params: { view, offset } }
  )
  return data
}

// Rail reads overlap (poll, focus, reply), so a slow older read must not
// overwrite the badge a newer read already set.
let badgeReadsStarted = 0
let badgeReadPublished = 0

const publishNeedsAttentionCount = (
  dispatch: Dispatch,
  read: number,
  count: number | undefined
): void => {
  if (count === undefined || read < badgeReadPublished) return
  badgeReadPublished = read
  dispatch(updateYourTurnCount(count))
}

export const fetchConversationView =
  (view: ConversationView, offset = 0): AdviserThunk =>
  async (dispatch: Dispatch) => {
    const read = ++badgeReadsStarted
    dispatch(conversationViewPending({ view }))
    try {
      const data = await getConversationView(view, offset)
      dispatch(
        conversationViewPageLoaded({
          view,
          offset,
          conversations: data.conversations,
          hasMore: data.hasMore,
          needsAttentionCount: data.needsAttentionCount,
        })
      )
      publishNeedsAttentionCount(dispatch, read, data.needsAttentionCount)
    } catch (err) {
      logHeardError(err, { context: 'adviser: fetch conversation view', view })
      dispatch(conversationViewFailed({ view }))
    }
  }

/** Re-reads a view page by page up to `depth` rows, stopping early when the server runs out. */
const readLoadedPages = async (
  view: ConversationView,
  depth: number
): Promise<ConversationViewResponse> => {
  const conversations: ConversationSummary[] = []
  const seen = new Set<number>()
  let hasMore = false
  let needsAttentionCount: number | undefined
  do {
    const data = await getConversationView(view, conversations.length)
    hasMore = data.hasMore
    needsAttentionCount = data.needsAttentionCount
    const fresh = data.conversations.filter((c) => !seen.has(c.id))
    for (const conversation of fresh) seen.add(conversation.id)
    conversations.push(...fresh)
    if (!fresh.length) break
  } while (hasMore && conversations.length < depth)
  return { conversations, hasMore, needsAttentionCount }
}

/**
 * Re-reads every page a view has already loaded. Reading only page one would drop
 * a row that a newer conversation pushed past it, with no Load more to reach it again.
 */
export const refreshConversationView =
  (view: ConversationView): AdviserThunk =>
  async (dispatch: Dispatch, getState: () => ReduxState) => {
    const loadedDepth = () =>
      getState().adviserChat.conversationViews[view].ids.length
    const read = ++badgeReadsStarted
    dispatch(conversationViewPending({ view }))
    try {
      let depth = loadedDepth()
      let data = await readLoadedPages(view, depth)
      // A Load more that lands mid-refresh deepens the view, and replacing it with
      // the shallower read would throw that page away.
      while (loadedDepth() > depth) {
        depth = loadedDepth()
        data = await readLoadedPages(view, depth)
      }
      dispatch(conversationViewReloaded({ view, ...data }))
      publishNeedsAttentionCount(dispatch, read, data.needsAttentionCount)
    } catch (err) {
      logHeardError(err, {
        context: 'adviser: refresh conversation view',
        view,
      })
      dispatch(conversationViewFailed({ view }))
    }
  }

/** Re-reads every rail view that has already been fetched. */
export const refreshConversationViews =
  (): AdviserThunk =>
  async (dispatch: Dispatch, getState: () => ReduxState) => {
    const views = getState().adviserChat.conversationViews
    await Promise.all(
      Object.values(CONVERSATION_VIEWS)
        .filter((view) => views[view].status !== 'idle')
        .map((view) => dispatch(refreshConversationView(view)))
    )
  }

export const fetchConversationDetail = async (
  id: number,
  params?: {
    history: 'recent'
    messagesCursor?: string
    caseEventsCursor?: string
  }
): Promise<ConversationDetailResponse | null> => {
  try {
    const { data } = await apiAxios.get<ConversationDetailResponse>(
      `${ADVISER_API_PATH}/conversations/${id}`,
      { params }
    )
    return data
  } catch (err) {
    logHeardError(err, {
      context: 'adviser: fetch conversation detail',
      conversationId: id,
    })
    return null
  }
}

export const deleteConversation =
  (id: number): AdviserThunk =>
  async (dispatch: Dispatch) => {
    dispatch(removeConversation(id))
    try {
      await apiAxios.delete(`${ADVISER_API_PATH}/conversations/${id}`)
    } catch (err) {
      logHeardError(err, {
        context: 'adviser: delete conversation',
        conversationId: id,
      })
      dispatch(fetchConversations())
      dispatch(refreshConversationViews())
    }
  }

export const submitFeedback = async (
  conversationId: number,
  payload: FeedbackPayload
): Promise<boolean> => {
  try {
    await apiAxios.post(
      `${ADVISER_API_PATH}/conversations/${conversationId}/feedback`,
      payload
    )
    return true
  } catch (err) {
    logHeardError(err, {
      context: 'adviser: submit feedback',
      conversationId,
    })
    return false
  }
}

export const resumeConversation =
  (
    id: number,
    options?: { keepLaunch?: boolean; history?: 'recent' }
  ): AdviserThunk<ConversationDetailResponse | null> =>
  (dispatch: Dispatch) => {
    dispatch(setActiveConversationId(id))
    if (!options?.keepLaunch) {
      dispatch(setActiveView('chat'))
      dispatch(conversationResumed())
    }
    return fetchConversationDetail(
      id,
      options?.history ? { history: options.history } : undefined
    )
  }

export const fetchMessageThreads = async (params: {
  tab?: MessageTab
  search?: string
  cursor?: MessageThreadCursor | null
}): Promise<MessageThreadsResponse | null> => {
  try {
    const { data } = await apiAxios.get<MessageThreadsResponse>(
      `${ADVISER_API_PATH}/tickets`,
      {
        params: {
          ...(params.tab ? { status: params.tab } : {}),
          ...(params.search ? { q: params.search } : {}),
          ...(params.cursor && params.cursor.page > 1
            ? { page: params.cursor.page }
            : {}),
          ...(params.cursor?.before ? { before: params.cursor.before } : {}),
        },
      }
    )
    return data
  } catch (err) {
    logHeardError(err, { context: 'adviser: fetch message threads' })
    return null
  }
}

export const fetchMessageThread = async (
  ticketId: number
): Promise<MessageThreadDetail | null> => {
  try {
    const { data } = await apiAxios.get<{ thread: MessageThreadDetail }>(
      `${ADVISER_API_PATH}/tickets/${ticketId}`
    )
    return data.thread
  } catch (err) {
    logHeardError(err, { context: 'adviser: fetch message thread', ticketId })
    return null
  }
}

export const fetchCatchUpWindow = async (): Promise<CatchUpWindow | null> => {
  const { data } = await apiAxios.get<{ window: CatchUpWindow | null }>(
    CATCH_UP_MONTHS_PATH
  )
  return data.window
}

interface PresortedUnsureResult {
  unsureCount: number
}

export const PRESORTED_MERCHANT_GONE = 'presorted_merchant_gone'

const PRESORTED_MERCHANT_GONE_ERROR_CODE = 'PRESORTED_MERCHANT_GONE'

export const sendPresortedToBookkeeper = (merchantKey: string, note: string) =>
  fetchWrapper({
    fetchKey: `SEND_PRESORTED_TO_BOOKKEEPER_${merchantKey}`,
    defaultErrorMessage: 'There was an error sending this to a bookkeeper.',
    fetchFunction: async () => {
      try {
        const { data } = await apiAxios.post<PresortedUnsureResult>(
          `/finances/api/v1/transactions/separation/presorted/${merchantKey}/unsure`,
          { note }
        )
        return data
      } catch (err) {
        // A retry after a send whose response was lost lands here, so it settles the card.
        if (parseErrorCodeFromCatch(err) === PRESORTED_MERCHANT_GONE_ERROR_CODE)
          return PRESORTED_MERCHANT_GONE
        throw err
      }
    },
  })
